import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowRight, Phone, CheckCircle, FolderOpen } from 'lucide-react';
import { LeadDto, leadsApi } from '../services/leadsService';
import { handleApiError } from '../utils/errorHandler';
import { hexToRgba } from '../utils/colorUtils';
import TabContainer from '../components/TabContainer';
import LeadModal from '../components/LeadModal';
import LeadHandlingPanel from '../components/leads/LeadHandlingPanel';
import LeadSchedulePanel from '../components/leads/LeadSchedulePanel';
import NewDocumentsTab from '../components/documents/NewDocumentsTab';

const PageContainer = styled.div`
  padding: 24px;
  direction: rtl;
  background: #ffffff;
  min-height: 100vh;

  @media (max-width: 767px) {
    padding: 16px 12px;
  }
`;

const PageHeader = styled.div`
  padding: 16px 24px;
  margin-bottom: 16px;

  @media (max-width: 767px) {
    padding: 12px 8px;
  }
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    gap: 12px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 8px;
  border-radius: var(--border-radius-md);
  transition: all 0.2s;
  width: 36px;
  height: 36px;
  flex-shrink: 0;

  &:hover {
    color: var(--primary-color);
    background: var(--hover-background);
  }
`;

const LeadInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const LeadName = styled.h1`
  font-size: 22px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 6px 0;

  @media (max-width: 767px) {
    font-size: 20px;
  }
`;

const LeadMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 14px;
  color: var(--text-secondary);
`;

const StatusBadge = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$color};
  background: ${props => hexToRgba(props.$color, 0.1)};
  border: 1px solid ${props => hexToRgba(props.$color, 0.25)};
`;

const PhoneLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;
  direction: ltr;

  &:hover {
    text-decoration: underline;
  }
`;

const ConvertedBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  padding: 10px 16px;
  background: var(--success-light);
  border: 1px solid var(--success-color);
  border-radius: var(--border-radius-md);
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const BannerText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const OutlineButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  background: var(--surface-color);
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;

  &:hover {
    background: var(--primary-color);
    color: white;
  }
`;

const DocumentsMovedNotice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 48px 24px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 15px;
`;

const ErrorMessage = styled.div`
  background: var(--error-light);
  color: #dc2626;
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 20px;
  border: 1px solid #dc2626;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 16px;
  color: var(--text-secondary);
`;

const DEFAULT_TAB = 'details';
const VALID_TABS = ['details', 'handling', 'schedule-tasks', 'documents'];

/**
 * עמוד ניהול ליד — מקביל לעמוד ניהול הלקוח. כל טאב הוא הקומפוננטה הקיימת של הליד:
 * פרטי ליד (LeadModal מוטמע), טיפול בליד, תיאום ומשימות, ומסמכים (אותו טאב מסמכים של הלקוח).
 */
const LeadManagementPage: React.FC = () => {
  const { leadId, tab, folderId } = useParams<{ leadId: string; tab?: string; folderId?: string }>();
  const navigate = useNavigate();

  const [lead, setLead] = useState<LeadDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<string>(() => {
    if (folderId) return 'documents';
    return tab || DEFAULT_TAB;
  });

  const loadLead = useCallback(async () => {
    if (!leadId) {
      setError('מזהה ליד חסר');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setLead(await leadsApi.getById(parseInt(leadId)));
    } catch (err) {
      setError(handleApiError(err, 'שגיאה בטעינת הליד'));
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  // רענון שקט אחרי שינוי (שמירה / פעילות / חתימה) — בלי מצב טעינה, כדי לא לאפס את הטאב הפתוח
  const refreshLead = useCallback(async () => {
    if (!leadId) return;
    try {
      setLead(await leadsApi.getById(parseInt(leadId)));
    } catch (err) {
      handleApiError(err, 'שגיאה ברענון הליד');
    }
  }, [leadId]);

  useEffect(() => {
    loadLead();
  }, [loadLead]);

  const handleTabChange = useCallback((newTab: string) => {
    setActiveTab(newTab);
    const newPath = newTab === DEFAULT_TAB
      ? `/leads/${leadId}/manage`
      : `/leads/${leadId}/manage/${newTab}`;
    navigate(newPath, { replace: true });
  }, [leadId, navigate]);

  // Sync tab with URL changes (e.g., browser back/forward, deep links)
  useEffect(() => {
    if (folderId) {
      setActiveTab('documents');
      return;
    }

    const currentTab = tab || DEFAULT_TAB;
    if (VALID_TABS.includes(currentTab)) {
      setActiveTab(currentTab);
    } else {
      setActiveTab(DEFAULT_TAB);
      navigate(`/leads/${leadId}/manage`, { replace: true });
    }
  }, [tab, folderId, leadId, navigate]);

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>טוען פרטי ליד...</LoadingContainer>
      </PageContainer>
    );
  }

  if (error || !lead) {
    return (
      <PageContainer>
        <ErrorMessage>{error || 'ליד לא נמצא'}</ErrorMessage>
        <OutlineButton onClick={() => navigate('/leads')}>חזרה לרשימת הלידים</OutlineButton>
      </PageContainer>
    );
  }

  const primaryPhone = lead.contacts?.find(c => c.isPrimary)?.number ?? lead.contacts?.[0]?.number;
  const convertedClientId = lead.clientId ?? null;

  const tabs = [
    {
      key: 'details',
      title: 'פרטי ליד',
      content: (
        <LeadModal
          embedded
          isOpen
          mode="view"
          lead={lead}
          onClose={() => undefined}
          onSave={refreshLead}
          onContractSigned={refreshLead}
        />
      )
    },
    {
      key: 'handling',
      title: 'טיפול בליד',
      content: <LeadHandlingPanel lead={lead} onLeadUpdated={refreshLead} />
    },
    {
      key: 'schedule-tasks',
      title: 'תיאום ומשימות',
      content: <LeadSchedulePanel lead={lead} />
    },
    {
      key: 'documents',
      title: 'מסמכים',
      content: convertedClientId ? (
        // אחרי החתימה התיקיות והמסמכים הועברו לתיק הלקוח — שם הם מנוהלים
        <DocumentsMovedNotice>
          <span>הליד חתם והפך ללקוח — התיקיות והמסמכים הועברו לתיק הלקוח.</span>
          <OutlineButton onClick={() => navigate(`/clients/${convertedClientId}/manage/documents`)}>
            <FolderOpen size={16} strokeWidth={2} />
            פתח את מסמכי הלקוח
          </OutlineButton>
        </DocumentsMovedNotice>
      ) : (
        <NewDocumentsTab
          owner={{ type: 'lead', id: lead.id }}
          folderId={folderId ? parseInt(folderId) : undefined}
        />
      )
    }
  ];

  return (
    <PageContainer>
      <PageHeader>
        <HeaderTop>
          <BackButton onClick={() => navigate('/leads')} title="חזרה ללידים">
            <ArrowRight size={20} />
          </BackButton>
          <LeadInfo>
            <LeadName>{lead.fullName}</LeadName>
            <LeadMeta>
              {lead.statusName && (
                <StatusBadge $color={lead.statusColor || '#6c757d'}>{lead.statusName}</StatusBadge>
              )}
              {primaryPhone && (
                <PhoneLink href={`tel:${primaryPhone}`}>
                  <Phone size={14} strokeWidth={2} />
                  {primaryPhone}
                </PhoneLink>
              )}
            </LeadMeta>
          </LeadInfo>
        </HeaderTop>

        {convertedClientId && (
          <ConvertedBanner>
            <BannerText>
              <CheckCircle size={18} strokeWidth={2} color="var(--success-color)" />
              הליד חתם והפך ללקוח
            </BannerText>
            <OutlineButton onClick={() => navigate(`/clients/${convertedClientId}/manage`)}>
              מעבר לתיק הלקוח
            </OutlineButton>
          </ConvertedBanner>
        )}
      </PageHeader>

      <TabContainer
        tabs={tabs}
        activeTab={activeTab}
        onChange={handleTabChange}
      />
    </PageContainer>
  );
};

export default LeadManagementPage;
